import { processStackTrace, processStackTraceArray } from './sourceMap';
import type { ErrorTrackResDto, StackTraceType } from './types';

/**
 * 解析堆栈跟踪
 * @param stack 堆栈信息
 * @returns 解析后的堆栈帧数组
 */
export function parseStackTrace(stack: string): Array<{
  fileName: string;
  lineNumber: number;
  columnNumber: number;
  functionName: string;
}> {
  const stackFrames: any[] = [];
  const stackLines = stack.split('\n');

  // Chrome/Firefox/Edge 堆栈格式
  const stackRegex = /at\s+(.*)\s+\((.*):(\d+):(\d+)\)/;
  // Safari 堆栈格式
  const safariRegex = /(.*)@(.*):(\d+):(\d+)/;

  for (const line of stackLines) {
    let match = line.match(stackRegex);
    if (!match) {
      match = line.match(safariRegex);
    }

    if (match) {
      const [, functionName, fileName, lineNumber, columnNumber] = match;
      stackFrames.push({
        functionName: functionName.trim() || 'anonymous',
        fileName: fileName.trim(),
        lineNumber: Number.parseInt(lineNumber, 10),
        columnNumber: Number.parseInt(columnNumber, 10),
      });
    }
  }

  return stackFrames;
}

/**
 * 从参数中提取 Error 对象
 * @param args 参数数组
 * @returns Error 对象或 null
 */
export function extractError(args: ErrorTrackResDto[]): Error | null {
  for (const arg of args) {
    if (arg instanceof Error) {
      return arg;
    } else if (arg?.stack && arg?.message) {
      // 类 Error 对象
      return arg as unknown as Error;
    } else if (typeof arg === 'string') {
      // 字符串形式的错误
      return new Error(arg);
    }
  }
  return null;
}

/**
 * JavaScript错误埋点
 * @param error 错误对象
 * @param errorMsg 错误描述
 * @param sourceMapEnable 是否启用 sourceMap
 * @param env 环境变量
 * @param track 回调函数，用于发送追踪数据
 */
export async function trackJSError(
  error: Error | string | StackTraceType[],
  errorMsg?: string,
  sourceMapEnable?: boolean,
  env?: string,
  track?: (data: any) => void,
): Promise<void> {
  let message = '未知错误';
  let stack: string | undefined;

  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message || '未知错误';
    stack = error.stack;
  } else if (errorMsg) {
    message = errorMsg;
  }
  let result: ErrorTrackResDto | null = null;
  // 处理堆栈信息，尝试使用 sourceMap 解析
  if (stack) {
    result = await processStackTrace(stack, !!sourceMapEnable, env, message);
  } else if (error instanceof Array) {
    result = await processStackTraceArray(error, !!sourceMapEnable, env, message);
  }
  if (track && result) {
    track({
      eventType: 'jsError',
      eventName: 'js_error',
      sysReportErrorInfoDto: result,
    });
  }
}
